<template>
  <div
    :class="controlClasses"
    class="mds-form-control mds-textarea"
    @mouseenter="showDisabledHint($event)"
    @mouseleave="hideDisabledHint()"
  >
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <textarea
          :disabled="disabled"
          :id="uid"
          :placeholder="placeholder"
          :value="modelValue"
          ref="textarea"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />

        <button @click="onClear" class="clear" type="button" v-if="showClear">
          <VIcon material="cancel" />
        </button>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VTextarea.scss" />

<script lang="ts">
import VBaseControl from "@/core/services/VBaseControl";

export default defineComponent({
  name: "VTextarea",

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model */
    modelValue: { type: String as PropType<string | null>, default: null },
    /** Кнопка очистки */
    clearable:  { type: Boolean, default: null },
    /** Изменять высоту в зависимости от набранного текста */
    autoHeight: { type: Boolean, default: false },
    /** Минимальная высота в пикселях */
    minHeight:  { type: Number,  default: 59 },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => ["left", "center", "right"].includes(value),
    },
  },

  emits: ['update:modelValue', 'input', 'blur', 'focus'],

  computed: {
    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true) classes.push("clearable");
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.modelValue !== null
        && this.modelValue !== ''
        && this.disabled === false;
    },

    /** labelStyles переопределяем — у инпута исторически width, а не flex. */
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },
  },

  watch: {
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.debounceValidation();
      }
    },
  },

  mounted() {
    this.$refs.textarea.style.minHeight = `${this.minHeight}px`;
    if (this.autoHeight === true) {
      this.resize();
    } else {
      this.$refs.textarea.style.overflowY = "auto";
    }
  },

  methods: {
    onInput(event) {
      event.stopPropagation();

      if (this.autoHeight === true) {
        this.resize();
      }

      const value = event.target.value;
      if (this.validation.isDirty === false && this.modelValue !== value) {
        this.validation.markDirty();
      }

      this.$emit('input', value);
      this.$emit("update:modelValue", value);
    },

    async onBlur(event) {
      if (typeof this.debounceValidation.cancel === "function") {
        this.debounceValidation.cancel();
      }
      await this.validate();
      this.$emit("blur", event.target.value);
    },

    onFocus(event) {
      this.validation.markTouched();
      this.$emit("focus", event);
    },

    onClear() {
      this.$emit("update:modelValue", null);
      this.$emit("blur", null);
      this.$nextTick(() => this.resize());
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.modelValue);
    },

    resize() {
      if (this.autoHeight === false) return;

      const elem      = this.$refs.textarea;
      const minHeight = parseInt(elem.style.minHeight);

      elem.style.height = "auto";

      let height = elem.scrollHeight;
      if (height < minHeight) {
        height = minHeight;
      }
      elem.style.height = `${height}px`;
    },
  },
});
</script>
